import { useCreate } from '@refinedev/core';
import { KeyRound, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { generateTotpCode, totpSecondsRemaining } from '@/lib/totp';
import { REGIONS, type Region } from '@/lib/types';

interface CreateForm {
  email: string;
  region: Region;
  password: string;
  dob?: string;
  psn_online_id?: string;
  totp_secret?: string;
  phone?: string;
  phone_provider?: string;
  notes?: string;
  recovery_codes_paste?: string;
}

export function PsnAccountCreatePage() {
  const navigate = useNavigate();
  const { mutate: create, isLoading } = useCreate();
  const [form, setForm] = useState<CreateForm>({
    email: '',
    region: 'BR',
    password: '',
  });

  const [totpCode, setTotpCode] = useState<string | null>(null);
  const [totpCountdown, setTotpCountdown] = useState(30);
  const [totpActive, setTotpActive] = useState(false);
  const [totpLoading, setTotpLoading] = useState(false);

  useEffect(() => {
    if (!totpActive || !form.totp_secret) return;

    let cancelled = false;
    const refresh = async () => {
      try {
        const code = await generateTotpCode(form.totp_secret ?? '');
        if (!cancelled) {
          setTotpCode(code);
          setTotpCountdown(totpSecondsRemaining());
        }
      } catch {
        if (!cancelled) {
          setTotpActive(false);
          setTotpCode(null);
        }
      }
    };
    const timer = window.setInterval(() => void refresh(), 1000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [form.totp_secret, totpActive]);

  const update = <K extends keyof CreateForm>(key: K, value: CreateForm[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const onTotpSecretChange = (value: string) => {
    update('totp_secret', value);
    setTotpActive(false);
    setTotpCode(null);
  };

  const onGenerateTotp = async () => {
    setTotpLoading(true);
    try {
      const code = await generateTotpCode(form.totp_secret ?? '');
      setTotpCode(code);
      setTotpCountdown(totpSecondsRemaining());
      setTotpActive(true);
    } catch {
      setTotpActive(false);
      setTotpCode(null);
      toast.error('Informe um TOTP Secret Base32 válido');
    } finally {
      setTotpLoading(false);
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      toast.error('Email e senha são obrigatórios');
      return;
    }

    let recoveryCodes: string[] | undefined;
    if (form.recovery_codes_paste?.trim()) {
      const lines = form.recovery_codes_paste
        .split(/[\n,;\s]+/)
        .map((c) => c.trim())
        .filter(Boolean);
      if (lines.length !== 10) {
        toast.error(`Esperado 10 recovery codes, recebido ${lines.length}. Cole 1 por linha.`);
        return;
      }
      recoveryCodes = lines;
    }

    const payload = {
      email: form.email.trim().toLowerCase(),
      region: form.region,
      password: form.password,
      dob: form.dob?.trim() || undefined,
      psn_online_id: form.psn_online_id?.trim() || undefined,
      totp_secret: form.totp_secret?.trim() || undefined,
      phone: form.phone?.trim() || undefined,
      phone_provider: form.phone_provider?.trim() || undefined,
      notes: form.notes?.trim() || undefined,
      recovery_codes: recoveryCodes,
    };

    create(
      { resource: 'psn-accounts', values: payload },
      {
        onSuccess: (result) => {
          toast.success('Conta criada com sucesso');
          const id = (result.data as { id?: string })?.id;
          if (id) navigate(`/contas/${id}`);
          else navigate('/contas');
        },
        onError: (err) => {
          const code = (err as unknown as { errorCode?: string })?.errorCode;
          if (code === 'email_exists') {
            toast.error('Já existe uma conta com este email.');
          } else {
            toast.error('Erro ao criar conta');
          }
        },
      },
    );
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <Button variant="ghost" size="sm" asChild className="mb-2">
          <Link to="/contas">← Voltar</Link>
        </Button>
        <h1 className="text-3xl font-bold tracking-tight">Nova conta PSN</h1>
        <p className="text-muted-foreground">
          Registrar manualmente uma conta criada à mão. O worker da Fase 2 vai popular automaticamente.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Identidade</CardTitle>
          <CardDescription>Email e região da conta. Senha fica encriptada (AES-256-GCM).</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="region">Região *</Label>
                <Select
                  value={form.region}
                  onValueChange={(v) => update('region', v as Region)}
                >
                  <SelectTrigger id="region">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REGIONS.map((r) => (
                      <SelectItem key={r} value={r}>
                        {r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="password">Senha *</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  value={form.password}
                  onChange={(e) => update('password', e.target.value)}
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dob">Data nasc. (YYYY-MM-DD)</Label>
                <Input
                  id="dob"
                  placeholder="1995-08-12"
                  value={form.dob ?? ''}
                  onChange={(e) => update('dob', e.target.value)}
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="psn_online_id">PSN Online ID</Label>
                <Input
                  id="psn_online_id"
                  value={form.psn_online_id ?? ''}
                  onChange={(e) => update('psn_online_id', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="totp_secret">TOTP Secret (Base32)</Label>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Input
                    id="totp_secret"
                    value={form.totp_secret ?? ''}
                    onChange={(e) => onTotpSecretChange(e.target.value)}
                    spellCheck={false}
                    autoComplete="off"
                    className="font-mono"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void onGenerateTotp()}
                    disabled={totpLoading || !form.totp_secret?.trim()}
                  >
                    {totpLoading ? <Loader2 className="animate-spin" /> : <KeyRound />}
                    Gerar código
                  </Button>
                </div>
                {totpCode && (
                  <div
                    className="flex items-center justify-between rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2"
                  >
                    <span
                      aria-live="polite"
                      className="font-mono text-2xl font-semibold tracking-[0.22em] text-emerald-400"
                    >
                      {totpCode}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      renova em {totpCountdown}s
                    </span>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  O código é gerado somente neste navegador e não é salvo.
                </p>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="phone">Telefone</Label>
                <Input
                  id="phone"
                  value={form.phone ?? ''}
                  onChange={(e) => update('phone', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone_provider">Provedor SMS</Label>
                <Input
                  id="phone_provider"
                  placeholder="manual / 5sim / sms-activate"
                  value={form.phone_provider ?? ''}
                  onChange={(e) => update('phone_provider', e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="recovery_codes_paste">Recovery codes (10 — opcional)</Label>
              <Textarea
                id="recovery_codes_paste"
                rows={6}
                placeholder={'Cole os 10 recovery codes do PSN, 1 por linha\nex:\nABCD-1234\nEFGH-5678\n...'}
                value={form.recovery_codes_paste ?? ''}
                onChange={(e) => update('recovery_codes_paste', e.target.value)}
                spellCheck={false}
                autoComplete="off"
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Cada code fica encriptado individualmente. Pode adicionar depois se preferir.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notas</Label>
              <Textarea
                id="notes"
                rows={3}
                value={form.notes ?? ''}
                onChange={(e) => update('notes', e.target.value)}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" asChild>
                <Link to="/contas">Cancelar</Link>
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Criar conta
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
